import React, { useState, useEffect, useRef } from 'react'
import { Text, Progress } from '@flatfile/baseline'
import { Header } from './Header'
import { GridAnimation, AnimationStoryboard } from '../../../components/GridAnimation/GridAnimation'

export type LoadingStage = 'initializing' | 'generating' | 'loading-space'

interface UnifiedLoadingScreenProps {
  stage: LoadingStage
  jobStatus?: string
  progress?: number
  hasAutobuildV2?: boolean
}

export const AutobuildLoadingScreen: React.FC<UnifiedLoadingScreenProps> = ({
  stage,
  jobStatus,
  progress,
  hasAutobuildV2,
}) => {
  // Track if component has initialized
  const isInitializedRef = useRef(false)

  // Track the storyboard based on stage/jobStatus - initialize with null to ensure first animation triggers
  const [storyboard, setStoryboard] = useState<AnimationStoryboard | null>(null)

  // Initialize with thinking animation
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true
      setStoryboard('thinking')
    }
  }, [])

  // Handle animation completion - always return to thinking
  const handleAnimationComplete = (completedStoryboard: AnimationStoryboard) => {
    // When any non-thinking animation completes, return to thinking
    if (completedStoryboard !== 'thinking') {
      setStoryboard('thinking')
    }
  }

  // Determine which animation to show based on props
  useEffect(() => {
    // Don't do anything if not initialized yet
    if (!isInitializedRef.current) return

    // Default is always thinking
    let nextStoryboard: AnimationStoryboard = 'thinking'

    // Override with special animations when needed
    if (jobStatus?.toLowerCase().includes('file')) {
      nextStoryboard = 'documentUpload'
    }
    if (stage === 'loading-space') {
      nextStoryboard = 'success'
    }

    // Only change the animation if needed
    if (storyboard !== nextStoryboard) {
      setStoryboard(nextStoryboard)
    }
  }, [stage, jobStatus, storyboard])

  // Get the content text based on current stage
  const getContentText = (): string => {
    switch (stage) {
      case 'initializing':
        return 'Initializing AutoBuild...'
      case 'generating':
        return jobStatus?.length ? jobStatus : 'Generating your workbook...'
      case 'loading-space':
        return 'Loading workbook...'
      default:
        return 'Loading...'
    }
  }

  return (
    <div className='flex flex-col h-full'>
      <Header />
      <div className='flex-1 flex flex-col items-center justify-center p-8'>
        <div className='flex flex-col items-center space-y-6 max-w-md'>
          {!!storyboard && (
            <>
              <div className='text-center space-y-4'>
                <Text variant='largeTitle'>{getContentText()}</Text>
                {progress !== undefined && hasAutobuildV2 && (
                  <div className='space-y-2 flex flex-col items-center max-w-md'>
                    <Progress value={progress} className='w-[660px] max-w-full' />
                    <Text variant='body'>{Math.round(progress)}%</Text>
                  </div>
                )}
              </div>
              <GridAnimation
                storyboard={storyboard}
                className='opacity-50'
                onAnimationComplete={handleAnimationComplete}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
